import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, Circle, useMapEvents } from 'react-leaflet';
import { Icon, LatLngBounds, divIcon, LatLng } from 'leaflet';
import { MapPin, Clock, Battery, Thermometer, Wifi, Activity, Navigation } from 'lucide-react';
import { GpsData, Geofence } from '../lib/supabase';
import { formatDistanceToNow, format } from 'date-fns';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface TraqPackMapProps {
  gpsData: GpsData[];
  timeFilter: string;
  loading: boolean;
  selectedDevice: string | null;
  geofences?: Geofence[];
  selectedGeofence?: string | null;
  isDrawingGeofence?: boolean;
  onGeofenceCreated?: (center: [number, number], radius: number) => void;
}

type MapType = 'street' | 'satellite';

// Custom marker icon for active devices with pulsating effect
const createCustomIcon = (isActive: boolean, deviceId: string, isSelected: boolean, isRecent: boolean = false) => {
  const color = isActive ? '#22C55E' : '#6B7280'; // green for online, gray for offline
  const size = isSelected ? 32 : 28;
  const pulseClass = isActive && isRecent ? 'marker-pulse-green' : '';
  
  return divIcon({
    html: `
      <div style="
        width: ${size + 20}px;
        height: ${size + 20}px;
        position: relative;
        z-index: ${isSelected ? 1000 : 100};
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        ${isActive && isRecent ? `
          <div class="${pulseClass}" style="
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            background: ${color};
            opacity: 0.3;
          "></div>
        ` : ''}
        <div style="
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="${size * 0.6}" height="${size * 0.6}" viewBox="0 0 24 24" fill="white">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>
      </div>
    `,
    className: '',
    iconSize: [size + 20, size + 20],
    iconAnchor: [(size + 20)/2, (size + 20)/2],
  });
};


// Component to handle geofence drawing
function GeofenceDrawer({ isDrawing, onGeofenceCreated }: { isDrawing?: boolean, onGeofenceCreated?: (center: [number, number], radius: number) => void }) {
  const [isDrawingLocal, setIsDrawingLocal] = useState(false);
  const [center, setCenter] = useState<LatLng | null>(null);
  const [radius, setRadius] = useState(0);
  
  const map = useMapEvents({
    mousedown: (e) => {
      if (isDrawing && !isDrawingLocal) {
        // Prevent default to stop map dragging
        if (e.originalEvent) {
          e.originalEvent.preventDefault();
          e.originalEvent.stopPropagation();
        }
        setIsDrawingLocal(true);
        setCenter(e.latlng);
        setRadius(0);
      }
    },
    mousemove: (e) => {
      if (isDrawing && isDrawingLocal && center) {
        const distance = center.distanceTo(e.latlng);
        setRadius(distance);
      }
    },
    mouseup: () => {
      if (isDrawing && isDrawingLocal && center && radius > 50) { // Minimum 50m radius
        // Only create geofence if we have a meaningful radius
        onGeofenceCreated?.([center.lat, center.lng], radius);
        setIsDrawingLocal(false);
        setCenter(null);
        setRadius(0);
      } else if (isDrawingLocal) {
        // Cancel if radius too small
        setIsDrawingLocal(false);
        setCenter(null);
        setRadius(0);
      }
    }
  });
  
  // Disable map dragging when drawing
  React.useEffect(() => {
    if (isDrawing && map) {
      map.dragging.disable();
      map.scrollWheelZoom.disable();
    } else if (map) {
      map.dragging.enable();
      map.scrollWheelZoom.enable();
    }
  }, [isDrawing, map]);
  
  if (!center) return null;
  
  return (
    <>
      {/* Center marker */}
      {center && (
        <Circle
          center={center}
          radius={5}
          pathOptions={{
            color: '#8B5CF6',
            fillColor: '#8B5CF6',
            fillOpacity: 1,
            weight: 2,
          }}
        />
      )}
      
      {/* Drawing circle */}
      {radius > 0 && (
        <Circle
          center={center}
          radius={radius}
          pathOptions={{
            color: '#8B5CF6',
            fillColor: '#8B5CF6',
            fillOpacity: 0.3,
            weight: 3,
            dashArray: '10, 10',
          }}
        />
      )}
      
      {/* Radius line */}
      {radius > 0 && (
        <Polyline
          positions={[
            [center.lat, center.lng],
            [center.lat + (radius / 111000), center.lng] // Approximate conversion to degrees
          ]}
          pathOptions={{
            color: '#8B5CF6',
            weight: 2,
            dashArray: '5, 5',
          }}
        />
      )}
      
      {/* Show radius text */}
      {radius > 0 && (
        <Marker
          position={center}
          icon={divIcon({
            html: `<div style="
              background: rgba(139, 92, 246, 0.9);
              padding: 4px 8px;
              border-radius: 4px;
              font-weight: bold;
              font-size: 12px;
              white-space: nowrap;
              transform: translate(-50%, -200%);
            ">${radius.toFixed(0)}m</div>`,
            className: '',
            iconSize: [0, 0],
          })}
        />
      )}
    </>
  );
}

// Component to auto-fit map bounds
function MapBounds({ gpsData, selectedDevice, timeFilter }: { gpsData: GpsData[], selectedDevice: string | null, timeFilter: string }) {
  const map = useMap();
  
  useEffect(() => {
    // Small delay to ensure map is ready
    const timer = setTimeout(() => {
      if (!map || gpsData.length === 0) return;
      
      // Force invalidate size to ensure map knows its dimensions
      map.invalidateSize();
      
      // Filter invalid coordinates (less aggressive)
      const validData = gpsData.filter(point => {
        // Basic validation
        const isDefaultLA = point.latitude === 34.0522 && point.longitude === -118.2437;
        const isInvalidCoords = point.latitude === 0 && point.longitude === 0;
        const isOutOfBounds = Math.abs(point.latitude) > 90 || Math.abs(point.longitude) > 180;
        
        // Check for exactly zero decimals (like 34.0000 or -118.0000)
        const isWholeNumber = Number.isInteger(point.latitude) || Number.isInteger(point.longitude);
        
        return !isDefaultLA && !isInvalidCoords && !isOutOfBounds && !isWholeNumber;
      });
      
      // If a device is selected, use ALL its data points for the time period
      // Otherwise, get the most recent point from each unique device
      let dataToFit: GpsData[];
      
      if (selectedDevice) {
        // IMPORTANT: Use ALL points from the selected device for proper bounds
        dataToFit = validData.filter(point => point.device_id === selectedDevice);
      } else {
        // Get the most recent point for each unique device
        const deviceMap = new Map<string, GpsData>();
        validData.forEach(point => {
          const existing = deviceMap.get(point.device_id);
          if (!existing || new Date(point.created_at) > new Date(existing.created_at)) {
            deviceMap.set(point.device_id, point);
          }
        });
        dataToFit = Array.from(deviceMap.values());
      }
      
      console.log(`[TraqPack] Filtered ${gpsData.length} to ${validData.length} valid points`);
      console.log(`[TraqPack] Fitting ${dataToFit.length} points for device: ${selectedDevice || 'all'}`);
      
      if (dataToFit.length === 0) return;
      
      // Skip outlier filtering to ensure all points are visible
      // Users need to see the complete journey for the selected time frame
      
      if (dataToFit.length === 1) {
        // Single point - just center on it with reasonable zoom
        const point = dataToFit[0];
        console.log(`[TraqPack] Single point at: ${point.latitude}, ${point.longitude}`);
        map.setView([point.latitude, point.longitude], 15, { animate: true });
      } else {
        // Multiple points - create bounds that include ALL points
        let minLat = dataToFit[0].latitude;
        let maxLat = dataToFit[0].latitude;
        let minLng = dataToFit[0].longitude;
        let maxLng = dataToFit[0].longitude;
        
        // Find the bounds that include ALL points
        dataToFit.forEach(point => {
          minLat = Math.min(minLat, point.latitude);
          maxLat = Math.max(maxLat, point.latitude);
          minLng = Math.min(minLng, point.longitude);
          maxLng = Math.max(maxLng, point.longitude);
        });
        
        // Add a more generous buffer to ensure edge points are not cut off
        const latBuffer = (maxLat - minLat) * 0.2; // 20% buffer for better visibility
        const lngBuffer = (maxLng - minLng) * 0.2; // 20% buffer for better visibility
        
        // Apply minimum buffer for very small areas
        const minBuffer = 0.005; // Approximately 500 meters
        const actualLatBuffer = Math.max(latBuffer, minBuffer);
        const actualLngBuffer = Math.max(lngBuffer, minBuffer);
        
        // Create bounds with buffer
        const bounds = new LatLngBounds(
          [minLat - actualLatBuffer, minLng - actualLngBuffer],
          [maxLat + actualLatBuffer, maxLng + actualLngBuffer]
        );
        
        // Calculate the size of the area
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;
        
        console.log(`[TraqPack] Bounds: SW(${minLat},${minLng}) to NE(${maxLat},${maxLng})`);
        console.log(`[TraqPack] Area size: ${latDiff}° x ${lngDiff}°`);
        console.log(`[TraqPack] Buffer applied: ${actualLatBuffer}° x ${actualLngBuffer}°`);
        
        // Fit the map to show all points with appropriate padding
        try {
          // Different zoom behavior for all devices vs single device
          if (!selectedDevice) {
            // For "All Devices" view - show global perspective
            if (latDiff > 20 || lngDiff > 20) {
              // Very large area (multiple continents) - use world view
              map.fitBounds(bounds, {
                padding: [30, 30] as [number, number],
                maxZoom: 5,
                animate: true
              });
            } else {
              // Smaller area - fit with reasonable zoom
              map.fitBounds(bounds, {
                padding: [50, 50] as [number, number],
                maxZoom: 10,
                animate: true
              });
            }
          } else {
            // For single device view - fit ALL points properly
            // Much more generous padding to ensure all points are visible
            const padding: [number, number] = [150, 150]; // Increased padding
            
            // Force fit bounds without any maxZoom restrictions initially
            map.fitBounds(bounds, {
              padding: padding,
              animate: true
            });
            
            // After fitting, check if we need to adjust zoom
            setTimeout(() => {
              const currentZoom = map.getZoom();
              
              // Only limit zoom for very large areas
              if (latDiff > 10 || lngDiff > 10) {
                // Very large distance - likely international travel
                if (currentZoom < 4) {
                  map.setZoom(4, { animate: true });
                }
              } else if (latDiff > 5 || lngDiff > 5) {
                // Country-wide travel
                if (currentZoom < 6) {
                  map.setZoom(6, { animate: true });
                }
              } else if (latDiff > 1 || lngDiff > 1) {
                // Regional travel
                if (currentZoom < 8) {
                  map.setZoom(8, { animate: true });
                }
              }
              // For local travel, let the fitBounds determine the best zoom
              
              console.log(`[TraqPack] Final zoom level: ${map.getZoom()} for area: ${latDiff}° x ${lngDiff}°`);
              
              // Double-check that all points are visible
              const mapBounds = map.getBounds();
              let allPointsVisible = true;
              dataToFit.forEach(point => {
                if (!mapBounds.contains([point.latitude, point.longitude])) {
                  allPointsVisible = false;
                  console.warn(`[TraqPack] Point at ${point.latitude}, ${point.longitude} is outside visible bounds`);
                }
              });
              
              // If not all points are visible, zoom out one more level
              if (!allPointsVisible) {
                console.log('[TraqPack] Not all points visible, zooming out...');
                map.setZoom(map.getZoom() - 1, { animate: true });
              }
            }, 700);
          }
        } catch (error) {
          console.error('Error fitting bounds:', error);
          // Fallback to center view
          const centerLat = (minLat + maxLat) / 2;
          const centerLng = (minLng + maxLng) / 2;
          const fallbackZoom = selectedDevice ? 13 : 5;
          map.setView([centerLat, centerLng], fallbackZoom, { animate: true });
        }
      }
    }, 300); // Slightly longer delay to ensure map is fully ready
    
    return () => clearTimeout(timer);
  }, [gpsData, map, selectedDevice, timeFilter]);
  
  return null;
}

const TraqPackMap: React.FC<TraqPackMapProps> = ({ 
  gpsData, 
  timeFilter, 
  loading, 
  selectedDevice,
  geofences,
  selectedGeofence,
  isDrawingGeofence,
  onGeofenceCreated,
}) => {
  const mapRef = useRef<any>(null);
  const [mapType, setMapType] = useState<MapType>('street');
  
  // Add map position persistence
  const [mapPosition, setMapPosition] = useState<{ center: [number, number]; zoom: number }>(() => {
    const saved = localStorage.getItem('traqpack-map-position');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved map position:', e);
      }
    }
    // Default to Israel center
    return { center: [31.7683, 35.2137], zoom: 8 };
  });

  // Save map position when it changes
  const handleMapMove = useCallback((e: any) => {
    const map = e.target;
    const center = map.getCenter();
    const zoom = map.getZoom();
    const newPosition = { 
      center: [center.lat, center.lng] as [number, number], 
      zoom 
    };
    setMapPosition(newPosition);
    localStorage.setItem('traqpack-map-position', JSON.stringify(newPosition));
  }, []);

  // Add event listeners when map is ready
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.on('moveend', handleMapMove);
      map.on('zoomend', handleMapMove);
      
      return () => {
        map.off('moveend', handleMapMove);
        map.off('zoomend', handleMapMove);
      };
    }
  }, [handleMapMove]);

  // Get tile layer configuration based on map type
  const getTileLayerConfig = (type: MapType) => {
    switch (type) {
      case 'satellite':
        return {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: '&copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
        };
      case 'street':
      default:
        return {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        };
    }
  };

  // Group data by device to create paths
  const devicePaths = React.useMemo(() => {
    const paths: { [deviceId: string]: GpsData[] } = {};
    
    // Filter out invalid coordinates with same logic as MapBounds
    const validGpsData = gpsData.filter(point => {
      // Basic validation
      const isDefaultLA = point.latitude === 34.0522 && point.longitude === -118.2437;
      const isInvalidCoords = point.latitude === 0 && point.longitude === 0;
      const isOutOfBounds = Math.abs(point.latitude) > 90 || Math.abs(point.longitude) > 180;
      
      // Check for exactly zero decimals
      const isWholeNumber = Number.isInteger(point.latitude) || Number.isInteger(point.longitude);
      
      return !isDefaultLA && !isInvalidCoords && !isOutOfBounds && !isWholeNumber;
    });
    
    validGpsData.forEach(point => {
      if (!paths[point.device_id]) {
        paths[point.device_id] = [];
      }
      paths[point.device_id].push(point);
    });
    
    // Sort each device's points by timestamp (oldest first for proper path drawing)
    Object.keys(paths).forEach(deviceId => {
      paths[deviceId].sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      console.log(`[TraqPack] Device ${deviceId} has ${paths[deviceId].length} points for path`);
      if (paths[deviceId].length > 0) {
        console.log(`[TraqPack] Path time range: ${paths[deviceId][0].created_at} to ${paths[deviceId][paths[deviceId].length - 1].created_at}`);
      }
    });
    
    return paths;
  }, [gpsData]);

  // Get latest position for each device
  const latestPositions = React.useMemo(() => {
    const latest: { [deviceId: string]: GpsData } = {};
    
    // Use the same filtering logic as devicePaths
    const validGpsData = gpsData.filter(point => {
      const isDefaultLA = point.latitude === 34.0522 && point.longitude === -118.2437;
      const isInvalidCoords = point.latitude === 0 && point.longitude === 0;
      const isOutOfBounds = Math.abs(point.latitude) > 90 || Math.abs(point.longitude) > 180;
      const isWholeNumber = Number.isInteger(point.latitude) || Number.isInteger(point.longitude);
      
      return !isDefaultLA && !isInvalidCoords && !isOutOfBounds && !isWholeNumber;
    });
    
    validGpsData.forEach(point => {
      if (!latest[point.device_id] || 
          new Date(point.created_at) > new Date(latest[point.device_id].created_at)) {
        latest[point.device_id] = point;
      }
    });
    
    return Object.values(latest);
  }, [gpsData]);

  const getDeviceStatus = (timestamp: string) => {
    const now = new Date();
    const lastSeen = new Date(timestamp);
    const diffHours = (now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60);
    return diffHours < 24; // Active if seen within 24 hours
  };


  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-traqpack-gray-100 rounded-apple">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-traqpack-yellow mx-auto mb-4"></div>
          <p className="text-traqpack-gray-600">Loading map data...</p>
        </div>
      </div>
    );
  }

  // Check if we have valid positions after filtering
  const hasValidData = latestPositions.length > 0 || Object.keys(devicePaths).some(deviceId => devicePaths[deviceId].length > 0);
  
  if (!hasValidData) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-traqpack-gray-100 rounded-apple">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-traqpack-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-traqpack-gray-700 mb-2">No GPS Data</h3>
          <p className="text-traqpack-gray-600">No valid location data found for the selected time period.</p>
        </div>
      </div>
    );
  }

  // Use saved position or first valid position as center
  const center: [number, number] = mapPosition.center;

  const tileConfig = getTileLayerConfig(mapType);

  return (
    <div className="relative w-full h-full">
      {/* Map Type Selector - Google Maps Style */}
      <div className="absolute top-4 right-4 flex bg-white rounded-lg shadow-lg border border-gray-300 overflow-hidden" style={{ zIndex: 999 }}>
        <button
          onClick={() => setMapType('street')}
          className={`px-3 py-2 text-xs font-medium transition-all border-r border-gray-300 ${
            mapType === 'street' 
              ? 'bg-blue-500 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
          title="Map"
        >
          Map
        </button>
        <button
          onClick={() => setMapType('satellite')}
          className={`px-3 py-2 text-xs font-medium transition-all ${
            mapType === 'satellite' 
              ? 'bg-white text-gray-700 hover:bg-gray-50' 
              : 'bg-blue-500 text-white'
          }`}
          title="Satellite"
        >
          Satellite
        </button>
      </div>

      <MapContainer
        ref={mapRef}
        center={center}
        zoom={mapPosition.zoom}
        style={{ height: '100%', width: '100%' }}
        className={`rounded-apple ${isDrawingGeofence ? 'drawing-geofence' : ''}`}
        whenReady={() => {
          // Map is ready, we'll add event listeners in useEffect
        }}
      >
        <TileLayer
          attribution={tileConfig.attribution}
          url={tileConfig.url}
        />
      
      {/* Geofence drawer */}
      <GeofenceDrawer isDrawing={isDrawingGeofence} onGeofenceCreated={onGeofenceCreated} />
      
      {/* Render existing geofences */}
      {(geofences || []).map((geofence) => (
        <Circle
          key={geofence.id}
          center={[geofence.latitude, geofence.longitude]}
          radius={geofence.radius}
          pathOptions={{
            color: selectedGeofence === geofence.id ? '#8B5CF6' : '#3B82F6',
            fillColor: selectedGeofence === geofence.id ? '#8B5CF6' : '#3B82F6',
            fillOpacity: geofence.active ? 0.2 : 0.1,
            weight: selectedGeofence === geofence.id ? 4 : 2,
            dashArray: geofence.active ? undefined : '5, 10',
          }}
        >
          <Popup>
            <div className="p-2 min-w-[200px]">
              <h3 className="font-semibold text-lg mb-2">{geofence.name}</h3>
              <div className="space-y-1 text-sm">
                <p>Status: <span className={`font-medium ${geofence.active ? 'text-green-600' : 'text-gray-500'}`}>
                  {geofence.active ? 'Active' : 'Inactive'}
                </span></p>
                <p>Radius: {geofence.radius.toFixed(0)}m</p>
                <p>Center: {geofence.latitude.toFixed(6)}, {geofence.longitude.toFixed(6)}</p>
                <p>Tracking: {geofence.devices?.length || 'All'} devices</p>
              </div>
            </div>
          </Popup>
        </Circle>
      ))}
      
      {/* Auto-fit bounds */}
      <MapBounds gpsData={gpsData} selectedDevice={selectedDevice} timeFilter={timeFilter} />
      
      {/* Draw paths for all devices or selected device */}
      {Object.entries(devicePaths).map(([deviceId, points]) => {
        // Skip if not the selected device (when one is selected) or if not enough points
        if ((selectedDevice && deviceId !== selectedDevice) || points.length < 2) return null;
        
        console.log(`[TraqPack] Drawing path for ${deviceId} with ${points.length} points`);
        
        // Function to calculate distance between two points (in km)
        const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
          const R = 6371; // Earth's radius in km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                    Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          return R * c;
        };
        
        // Filter path segments to remove jumps > 50km (likely bad data)
        const pathSegments: [number, number][][] = [];
        const pathPoints: GpsData[] = []; // Store points for markers
        let currentSegment: [number, number][] = [[points[0].latitude, points[0].longitude]];
        pathPoints.push(points[0]);
        
        for (let i = 1; i < points.length; i++) {
          const prevPoint = points[i - 1];
          const currPoint = points[i];
          const distance = calculateDistance(
            prevPoint.latitude, prevPoint.longitude,
            currPoint.latitude, currPoint.longitude
          );
          
          // If distance is reasonable (< 50km for more strict filtering), add to current segment
          if (distance < 50) {
            currentSegment.push([currPoint.latitude, currPoint.longitude]);
            pathPoints.push(currPoint);
          } else {
            console.log(`[TraqPack] Skipping jump of ${distance.toFixed(1)}km between points`);
            // Start a new segment if the jump is too large
            if (currentSegment.length > 1) {
              pathSegments.push(currentSegment);
            }
            currentSegment = [[currPoint.latitude, currPoint.longitude]];
            pathPoints.push(currPoint);
          }
        }
        
        // Add the last segment if it has more than one point
        if (currentSegment.length > 1) {
          pathSegments.push(currentSegment);
        }
        
        console.log(`[TraqPack] Created ${pathSegments.length} path segments`);
        
        // Count how many dots will be shown
        const dotsShown = pathPoints.filter((_, index) => {
          const isStart = index === 0;
          const isEnd = index === pathPoints.length - 1;
          const showIntermediate = pathPoints.length <= 50 || index % Math.ceil(pathPoints.length / 50) === 0;
          return isStart || isEnd || showIntermediate;
        }).length;
        
        console.log(`[TraqPack] Showing ${dotsShown} dots out of ${pathPoints.length} total points`);
        
        // Determine path color based on whether device is selected
        const pathColor = selectedDevice === deviceId ? '#FFD700' : '#3B82F6'; // Yellow for selected, blue for others
        
        return (
          <React.Fragment key={`device-path-${deviceId}`}>
            {/* Draw the path lines */}
            {pathSegments.map((segment, index) => (
              <Polyline
                key={`path-${deviceId}-${index}`}
                positions={segment}
                pathOptions={{
                  color: pathColor,
                  weight: selectedDevice === deviceId ? 5 : 3,
                  opacity: selectedDevice === deviceId ? 0.9 : 0.6,
                  // Remove dashArray for solid line
                }}
              />
            ))}
            
            {/* Add markers for each GPS reading */}
            {pathPoints.map((point, index) => {
              const isStart = index === 0;
              const isEnd = index === pathPoints.length - 1;
              
              // For paths with many points, only show every nth point plus start/end
              const showIntermediate = pathPoints.length <= 50 || index % Math.ceil(pathPoints.length / 50) === 0;
              if (!isStart && !isEnd && !showIntermediate) return null;
              
              return (
                <Marker
                  key={`path-marker-${deviceId}-${index}`}
                  position={[point.latitude, point.longitude]}
                  icon={divIcon({
                    html: `
                      <div style="
                        background-color: ${isStart ? '#10B981' : isEnd ? '#EF4444' : pathColor};
                        width: ${isStart || isEnd ? 20 : 8}px;
                        height: ${isStart || isEnd ? 20 : 8}px;
                        border-radius: 50%;
                        border: 2px solid white;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
                        z-index: ${isStart || isEnd ? 1000 : 100};
                      "></div>
                    `,
                    className: '',
                    iconSize: isStart || isEnd ? [20, 20] : [8, 8],
                    iconAnchor: isStart || isEnd ? [10, 10] : [4, 4],
                  })}
                >
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">
                        {isStart ? '🟢 Start' : isEnd ? '🔴 End' : `Reading ${index + 1}`}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {format(new Date(point.created_at), 'MMM dd, HH:mm:ss')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                      </div>
                      {point.speed > 0 && (
                        <div className="text-xs text-gray-600 mt-1">
                          Speed: {point.speed} km/h
                        </div>
                      )}
                      {point.battery_percentage && (
                        <div className="text-xs text-gray-600">
                          Battery: {point.battery_percentage}%
                        </div>
                      )}
                      {point.address && (
                        <div className="text-xs text-gray-600 mt-1">
                          {point.address}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </React.Fragment>
        );
      })}
      
      {/* Show markers for latest positions - all devices or selected */}
      {latestPositions.map((point) => {
        // Show all markers when no device is selected, or only the selected device
        const shouldShowMarker = !selectedDevice || selectedDevice === point.device_id;
        
        if (!shouldShowMarker) return null;
        
        const isActive = getDeviceStatus(point.created_at);
        const isSelected = selectedDevice === point.device_id;
        const now = new Date();
        const lastSeen = new Date(point.created_at);
        const diffMinutes = (now.getTime() - lastSeen.getTime()) / (1000 * 60);
        const isRecent = diffMinutes < 5; // Recent if seen within 5 minutes
        
        return (
          <Marker
            key={`marker-${point.device_id}`}
            position={[point.latitude, point.longitude]}
            icon={createCustomIcon(isActive, point.device_id, isSelected, isRecent)}
          >
            <Popup 
              closeButton={false}
              className="custom-popup"
            >
              <div className="bg-white rounded-xl shadow-lg border-0 overflow-hidden min-w-[280px] max-w-[320px]">
                {/* Header with device status */}
                <div className={`px-4 py-3 text-white font-medium flex items-center justify-between ${
                  isActive ? 'bg-blue-500' : 'bg-gray-500'
                }`}>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-white' : 'bg-white/70'} animate-pulse`}></div>
                    <span className="font-semibold">Device {point.device_id}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Wifi className="w-4 h-4" />
                    <span className="text-xs">{isActive ? 'Online' : 'Offline'}</span>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>{format(new Date(point.created_at), 'yyyy-MM-dd HH:mm:ss')}</span>
                    </div>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded-full whitespace-nowrap">
                      {formatDistanceToNow(new Date(point.created_at))} ago
                    </span>
                  </div>
                </div>

                {/* Main content */}
                <div className="p-4 space-y-3">
                  {/* Location */}
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <MapPin className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">Location</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {point.address || 'No address available'}
                      </div>
                      <div className="text-xs font-mono text-gray-500 mt-1">
                        {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
                      </div>
                    </div>
                  </div>

                  {/* Speed */}
                  {point.speed > 0 && (
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <Navigation className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 flex justify-between items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">Speed</div>
                          <div className="text-xs text-gray-600">Current velocity</div>
                        </div>
                        <div className="text-lg font-bold text-purple-600">
                          {point.speed} km/h
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Battery */}
                  {point.battery_percentage && (
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        point.battery_percentage > 50 ? 'bg-green-50' : 
                        point.battery_percentage > 20 ? 'bg-yellow-50' : 'bg-red-50'
                      }`}>
                        <Battery className={`w-4 h-4 ${
                          point.battery_percentage > 50 ? 'text-green-600' : 
                          point.battery_percentage > 20 ? 'text-yellow-600' : 'text-red-600'
                        }`} />
                      </div>
                      <div className="flex-1 flex justify-between items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">Battery</div>
                          <div className="text-xs text-gray-600">Power level</div>
                        </div>
                        <div className={`text-lg font-bold ${
                          point.battery_percentage > 50 ? 'text-green-600' : 
                          point.battery_percentage > 20 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {point.battery_percentage}%
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Temperature */}
                  {point.temperature && (
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-orange-50 rounded-lg">
                        <Thermometer className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="flex-1 flex justify-between items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">Temperature</div>
                          <div className="text-xs text-gray-600">Device sensor</div>
                        </div>
                        <div className="text-lg font-bold text-orange-600">
                          {point.temperature}°C
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer with IMEI */}
                <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>IMEI: {point.raw_data?.imei || point.device_id}</span>
                    <div className="flex items-center space-x-1">
                      <Activity className="w-3 h-3" />
                      <span>GPS Mode</span>
                    </div>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
    </div>
  );
};

export default TraqPackMap;